import { useMemo, useRef } from "react";
import Accordion from "../Accordion";
import AnchorBar from '../AnchorBar';
import Presentation from '../Presentation';
import { useIsMobile } from '../Layout/useIsMobile';
import Slider from '../Slider';

import './PageProjectBlissimGift.scss';
import '../common.scss';

export const PageProjectBlissimGift = () => {
  const isMobile = useIsMobile();
  const contextRef = useRef(null);
  const resultsRef = useRef(null);
  const searchRef = useRef(null);
  const conclusionRef = useRef(null);
  const sliderItems = useMemo(() => [
    {
      classSuffix: "blissim-rgpd",
      image: "/images/Blissim/RGPD/presentation-mobile.png",
      subtitle: "DESIGN LÉGAL",
      title: "Blissim - RGPD",
      url: "/projet/blissim-rgpd"
    },
    {
      classSuffix: "indigo-neo",
      image: "/images/IndigoNeo/presentation-mobile.png",
      subtitle: "REFONTE APP / WEB",
      title: "Indigo Neo",
      url: "/projet/indigo-neo"
    },
    {
      classSuffix: "o-revoir",
      image: "/images/ORevoir/presentation-mobile.png",
      subtitle: "BUSINESS PLAN",
      title: "ô revoir",
      url: "/projet/o-revoir"
    },
  ], []);

  const anchors = useMemo(() => ([
    { id: "context", ref: contextRef, title: "Contexte" },
    { id: "search", ref: searchRef, title: "Recherches" },
    { id: "results", ref: resultsRef, title: "Résultats" },
    { id: "conclusion", ref: conclusionRef, title: "Conclusion" },
  ]), []);

  const problemText = "Les utilisateurs manquent de repères pour comparer les formules cadeaux et ne perçoivent pas clairement l’intérêt du 4 mois, ce qui freine leur choix. Il est nécessaire de mieux hiérarchiser et expliciter la valeur des durées, pour fluidifier leur décision.";
  const problemTitle = "Problème à résoudre";

  return (
    <div className="page-project blissim-gift">
      <Presentation
        alt="Blissim gift icon"
        classSuffix="blissim-gift"
        image={`/images/Blissim/Gift/presentation${isMobile ? '-mobile' : '-desktop'}.png`}
        title="Blissim - Gift"
        subtitle="REFONTE WEB"
      />

      <AnchorBar anchors={anchors} />

      <section className="anchor-section">
        <h2 ref={contextRef} id="discover" className="anchor--title">Contexte</h2>
        <hr className="anchor--separator" />

        <div className="discover-information">
          <div id="target" className="discover-information-wrapper">
            <h3 className="discover-information--title-first">Objectifs</h3>
            <p className="discover-information--description">
              Créer un parcours d’achat cadeau plus fluide, rassurant et engageant, afin d'augmenter le nombre d'achat sur la période de Noël.
            </p>
          </div>

          <div id="tools" className="discover-information-wrapper">
            <h3 className="discover-information--title">Outils</h3>
            <div className="discover-information--list">
              <div className="discover-information-list-item">
                <img className="icon-tool" src="/images/icon-figma.svg" alt="icon Figma" />
                <span className="icon-tool-title">Figma</span>
              </div>

              <div className="discover-information-list-item">
                <img className="icon-tool" src="/images/Blissim/Gift/abtasty.svg" alt="icon Photoshop" />
                <span className="icon-tool-title">AB tasty</span>
              </div> 
            </div>
          </div>

          <div id="duration" className="discover-information-wrapper">
            <h3 className="discover-information--title">Durée</h3>
            <p className="discover-information--description">
            ~ 4 semaines
            </p>
          </div>
        </div>

        <div id="problem" className="discover-information-wrapper">
          <Accordion
            accordionName="accordion--discover-information__problem"
            content={problemText}
            label={problemTitle}
          >
            <>
              <h3 className="discover-information--title">{problemTitle}</h3>
              <p className="discover-information--description__full-size" dangerouslySetInnerHTML={{ __html: problemText }} />
            </>
          </Accordion>
        </div>
      </section>

      <section className="anchor-section">
        <h2 ref={searchRef} id="define" className="anchor--title">Recherches</h2>
        <hr className="anchor--separator" />

        <div className="generic-flex-wrapper define-wrapper">
          <div className="define--block">
            <h3 className="define--title">Besoins sur le projet :</h3>
              <h4 className="define-block--subtitle">💼 Côté business</h4>
              <ul>
                <li>Vendre davantage de cartes cadeaux 4 mois pour équilibrer les formules.</li>
                <li>Stimuler les ventes globales, notamment pendant la période clé de fin d’année.</li>
              </ul>
              <h4 className="define-block--subtitle">🧑‍💻 Côté utilisateur</h4>
              <ul>
                <li>Clarifier le message pour qu’il comprenne facilement les différences entre durées, coffrets et modes de livraison.</li>
                <li>Rendre le parcours plus fluide et rassurant pour l’aider à comparer et choisir sereinement.</li>
              </ul>
          </div>

          <div className="define--block">
            <h3 className="define--title">Diagnostic :</h3>
            <div className="generic-flex-wrapper define-diagnostic--wrapper">
              <div className="define--diagnostic-card define--diagnostic-card__half-screen">
                <div className="define--diagnostic-card__value">15%</div>
                <div className="define--diagnostic-card__title">Clics non valides</div>
                <div className="define--diagnostic-card__text">Parcours confus, trop d’éléments non cliquables</div>
              </div>

              <div className="define--diagnostic-card define--diagnostic-card__half-screen">
                <div className="define--diagnostic-card__value">1,3%</div>
                <div className="define--diagnostic-card__title">Clics de colère</div>
                <div className="define--diagnostic-card__text">Frustration élevée, UI pas intuitive</div>
              </div>
            </div>

            <div className="define--diagnostic-card">
              <div className="define--diagnostic-card__value">2%</div>
              <div className="define--diagnostic-card__title">Retours rapides</div>
              <div className="define--diagnostic-card__text">Perte d’intérêt dès la 1ère impression</div>
            </div>
          </div>
        </div>

        <h3 className="define--title">Benchmark</h3>
        <div className="generic-flex-wrapper define-wrapper">
          <div className="define--benchmark-card define--benchmark-card__yellow">
            <div className="define--benchmark-card__text">
              <h4 className="define--benchmark-card__title">Petit ballon</h4>
              <p className="define--benchmark-card__subtitle">🔍 Observations :</p>
              <ul>
                <li>Parcours d’achat clair et multi-étapes.</li>
                <li>Offre segmentée et lisible, facile à comparer.</li>
                <li>Cross selling en fin de parcours pour enrichir le panier.</li>
              </ul>
              <p className="define--benchmark-card__subtitle">✅ Impact UX :</p>
              <p>
                Décision plus rapide, moins de frictions et une expérience qui rassure
                et incite à conclure l'achat.
              </p>
            </div>
            <img alt="Petit Ballon" className="define--benchmark-card__image" src="/images/Blissim/Gift/petit-ballon.jpeg" />
          </div>

          <div className="define--benchmark-card define--benchmark-card__green">
            <div className="define--benchmark-card__text">
              <h4 className="define--benchmark-card__title">Pandacraft</h4>
              <p className="define--benchmark-card__subtitle">🔍 Observations :</p>
              <ul>
                <li>Parcours en étapes qui simplifie et allège la décision.</li>
                <li>Formules présentées de façon claire et hiérarchisée, guidant naturellement le choix.</li>
              </ul>
              <p className="define--benchmark-card__subtitle">✅ Impact UX :</p>
              <p>
                Une navigation intuitive, un choix facilité et une offre mise en avant
                pour optimiser la conversion.
              </p>
            </div>
            <img alt="Pandacraft" className="define--benchmark-card__image" src="/images/Blissim/Gift/pandacraft.jpeg" />
          </div>
        </div>

        <h3 className="define--title">Principes UX identifiés</h3>
        <div className="generic-flex-wrapper define-wrapper">
          <div className="define-principle--card">
            <img alt="Loi de Hick" className="define-principle--card__image" src="/images/Blissim/sablier.svg" />
            <h4 className="define-principle--card__title">Loi de Hick</h4>
            <p className="define-principle--card__subtitle">Le temps pour prendre une décision augmente avec le nombre d’options.</p>
          </div>

          <div className="define-principle--card">
            <img alt="Effet Von Restorff" className="define-principle--card__image" src="/images/Blissim/cible.svg" />
            <h4 className="define-principle--card__title">Effet Von Restorff</h4>
            <p className="define-principle--card__subtitle">Un élément distinct a plus de chances d’être mémorisé.</p>
          </div>

          <div className="define-principle--card">
            <img alt="Preuve sociale" className="define-principle--card__image" src="/images/Blissim/people.svg" />
            <h4 className="define-principle--card__title">Preuve sociale implicite</h4>
            <p className="define-principle--card__subtitle">Les gens tendent à suivre le comportement des autres.</p>
          </div>
        </div>

        <h3 className="define--title">Mes constats</h3>
        <div id="target" className="generic-flex-wrapper define-wrapper">
          <div className="target--content">
            <h4 className="target-content--title">📚 Trop d’informations dispersées</h4>
            <ul>
              <li>Textes et visuels nombreux qui surchargent la page.</li>
              <li>Couleurs variées sans hiérarchie, ne guidant pas le regard vers une offre en particulier.</li>
            </ul>
            <h4 className="target-content--title">⬇️ Action mal priorisée</h4>
            <ul>
              <li>Les formules « offrir » sont placées bas, obligeant à scroller pour avancer.</li>
            </ul>
            <h4 className="target-content--title">💡 Orientations pour améliorer l’expérience</h4>
            <ul>
              <li>Mettre en avant durée et économies pour faciliter la comparaison.</li>
              <li>Ajouter un sélecteur clair et des badges pour guider le choix.</li>
              <li>Repenser la hiérarchie visuelle : prix d’abord, explications ensuite.</li>
            </ul>
          </div>
          <img alt="Page offrir ancienne version" className="target-content-image" src="/images/Blissim/Gift/old-page-offrir2.jpeg" />
        </div>
      </section>

      <section className="anchor-section">
        <h2 ref={resultsRef} id="design" className="anchor--title">Résultats</h2>
        <hr className="anchor--separator" />

        <div className="generic-flex-wrapper design-wrapper">
          <div className="design-container design-container__half-screen">
            <h4>Hero avant</h4>
            <div className="generic-flex-wrapper design--image-container">
              <img alt="Ancienne page gift mobile" className="design--image" src="/images/Blissim/Gift/blissim-gift-1-before.png" />
            </div>
          </div>
          <div className="design-container design-container__half-screen">
            <h4>Hero après</h4>
            <div className="generic-flex-wrapper design--image-container">
              <img alt="Nouvelle page gift mobile" className="design--image" src="/images/Blissim/Gift/blissim-gift-1-after.png" />
            </div>
          </div>
        </div>

        <div className="generic-flex-wrapper design-wrapper">
          <div className="design-container design-container__half-screen">
            <h4>Formules gift avant</h4>
            <div className="generic-flex-wrapper design--image-container">
              <img alt="Ancien plan gift mobile" className="design--image" src="/images/Blissim/Gift/blissim-gift-2-before.png" />
            </div>
          </div>
          <div className="design-container design-container__half-screen">
            <h4>Formules gift après</h4>
            <div className="generic-flex-wrapper design--image-container">
              <img alt="Nouveau plan gift mobile" className="design--image" src="/images/Blissim/Gift/blissim-gift-2-after.png" />
            </div>
          </div>
        </div>

        <div className="video--wrapper">
          <video controls src="/images/Blissim/Gift/videogift.mp4" type="video/mp4" />
        </div>
        <div className="generic-flex-wrapper design-wrapper">
          <div className="design-container" style={{ width: '100%' }}>
            <div className="generic-flex-wrapper design--image-container">
              <img alt="Version desktop" className="design--image design--image-desktop" src="/images/Blissim/Gift/blissim-gift-desktop.png" />
            </div>
          </div>
        </div>
      </section>

      <section className="anchor-section">
        <h2 ref={conclusionRef} id="realize" className="anchor--title">Conclusion</h2>
        <hr className="anchor--separator" />

        <div className="generic-flex-wrapper define-wrapper">
          <div className="define--benchmark-card define--benchmark-card__yellow">
            <div className="define--benchmark-card__text">
              <h4 className="define--benchmark-card__title">Une expérience plus claire, plus engageante</h4>
              <p>
                Cette refonte du parcours carte cadeau chez Blissim répond à deux objectifs clés :
              </p>
              <ul>
                <li>🎯 Fluidifier l’expérience utilisateur grâce à une meilleure hiérarchisation et des choix plus lisibles.</li>
                <li>💰 Rééquilibrer les ventes vers l’offre 4 mois, plus stratégique pour la marque.</li>
              </ul>
              <p>Grâce à l’application de principes UX forts (Hick, Von Restorff, Preuve sociale), nous avons posé les bases d’un parcours à la fois engageant, rassurant et orienté conversion.</p>
            </div>
          </div>

          <div className="define--benchmark-card define--benchmark-card__green">
            <div className="define--benchmark-card__text">
              <h4 className="define--benchmark-card__title">À suivre : A/B tests & itérations</h4>
              <p>Le projet est actuellement en cours de développement. Plusieurs tests seront menés pour mesurer :</p>
              <ul>
                <li>📈 L’impact du design sur le taux de clics de l’offre 4 mois</li>
                <li>🧭 La réduction des clics non valides et des abandons</li>
                <li>🛍️ La performance en période stratégique (campagne Q4)</li>
              </ul>
              <p>👉 Premiers résultats attendus fin d’année, une prochaine étape pour affiner encore l’expérience.</p>
            </div>
          </div>
        </div>
      </section>

      <Slider sliderItems={sliderItems} />
    </div>
  );
};

export default PageProjectBlissimGift;